import Head from 'next/head';
import { FormEvent, ReactNode } from 'react';

import { GladlyChat } from '../GladlyChat/GladlyChat';
import { getKnowledgeSiteConfig } from '../../utils/knowledge/knowledgeSiteConfig';
import { getKnowledgeCopy, KnowledgeLocale } from '../../utils/knowledge/knowledgeCopy';
import { getKnowledgeIndexLink } from '../../utils/knowledge/knowledgeArticles';
import { KnowledgeChannel } from '../../utils/knowledge/types';
import {
  KnowledgeCentreFooter,
  KnowledgeCentreHeader,
  KnowledgeCentreLogo,
  KnowledgeCentreNav,
  KnowledgeCentreNavLink,
  KnowledgeCentreNavList,
  KnowledgeCentreRoot,
  KnowledgeSearchBar,
  KnowledgeSearchButton,
  KnowledgeSearchInput,
  KnowledgeSearchResultLink,
  KnowledgeSearchResults,
} from './KnowledgeCentre.styles';

interface KnowledgeSearchResult {
  urlName: string;
  title: string;
}

interface KnowledgeCentreLayoutProps {
  channel: KnowledgeChannel;
  locale: KnowledgeLocale;
  title: string;
  description?: string;
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  searchResults?: KnowledgeSearchResult[];
  children: ReactNode;
}

export const KnowledgeCentreLayout = ({
  channel,
  locale,
  title,
  description,
  searchQuery,
  onSearchQueryChange,
  searchResults = [],
  children,
}: KnowledgeCentreLayoutProps) => {
  const site = getKnowledgeSiteConfig(channel, locale);
  const ui = getKnowledgeCopy(locale).ui;
  const indexHref = getKnowledgeIndexLink(locale, channel);
  const channelProp = { $channel: channel };

  const handleSearchSubmit = (event: FormEvent) => {
    event.preventDefault();
  };

  return (
    <KnowledgeCentreRoot>
      <Head>
        <title>{title}</title>
        {description ? <meta name="description" content={description} /> : null}
        <link
          href="https://fonts.googleapis.com/css2?family=Lato:wght@400;700&display=swap"
          rel="stylesheet"
        />
      </Head>
      <GladlyChat />
      <KnowledgeCentreHeader {...channelProp}>
        <KnowledgeCentreLogo href={indexHref} $logoSrc={site.logoSrc} {...channelProp} aria-label={`${site.siteName} home`}>
          <span aria-hidden />
        </KnowledgeCentreLogo>
        <KnowledgeSearchBar>
          <KnowledgeSearchInput
            {...channelProp}
            type="search"
            placeholder={ui.searchPlaceholder}
            value={searchQuery}
            onChange={(e) => onSearchQueryChange(e.target.value)}
            aria-label={ui.searchAriaLabel}
          />
          <KnowledgeSearchButton {...channelProp} type="button" onClick={handleSearchSubmit}>
            {ui.searchButton}
          </KnowledgeSearchButton>
        </KnowledgeSearchBar>
      </KnowledgeCentreHeader>
      <KnowledgeCentreNav {...channelProp}>
        <KnowledgeCentreNavList>
          <li>
            <KnowledgeCentreNavLink
              href={indexHref}
              className="nav-home"
              {...channelProp}
              aria-label={ui.homeAriaLabel}
            >
              ⌂
            </KnowledgeCentreNavLink>
          </li>
          <li>
            <KnowledgeCentreNavLink href={indexHref} {...channelProp}>
              {channel === 'hero' ? ui.home : site.navBrandLabel}
            </KnowledgeCentreNavLink>
          </li>
          <li>
            <KnowledgeCentreNavLink href={indexHref} {...channelProp}>
              {ui.topicCatalog}
            </KnowledgeCentreNavLink>
          </li>
        </KnowledgeCentreNavList>
      </KnowledgeCentreNav>
      {searchResults.length > 0 ? (
        <KnowledgeSearchResults {...channelProp}>
          <ul>
            {searchResults.map((result) => (
              <li key={result.urlName}>
                <KnowledgeSearchResultLink
                  href={`/${locale}/${channel}/article/${result.urlName}`}
                >
                  {result.title}
                </KnowledgeSearchResultLink>
              </li>
            ))}
          </ul>
        </KnowledgeSearchResults>
      ) : null}
      <main>{children}</main>
      <KnowledgeCentreFooter {...channelProp}>
        <p>{site.siteName}</p>
      </KnowledgeCentreFooter>
    </KnowledgeCentreRoot>
  );
};
